import {Injectable} from "@angular/core";
import {IResult as UAParserOriginalResult, UAParser} from "ua-parser-js";


@Injectable({
  providedIn: "root",
})
export class DeviceDetectService {

  private readonly _isMobile: boolean;

  constructor() {
    this._isMobile = this.calcIsMobile();
  }

  get isMobile() {
    return this._isMobile;
  }

  /**
   * Retrieves the parsed user agent information.
   *
   * @return {UAParserResult} The transformed result containing the user agent details.
   */
  get userAgent(): UAParserResult {
    const result = this.parseUserAgent();
    return this.transformUAParserResult(result);
  }

  /**
   * Determines if the user agent is mobile or not.
   *
   * @return {boolean} True if the user agent is mobile, false otherwise.
   * @private
   */
  private calcIsMobile(): boolean {
    const deviceType = this.parseUserAgent().device.type;
    return deviceType === 'mobile' || deviceType === 'tablet' || deviceType === 'wearable';
  }

  /**
   * Parses the user agent string to extract information about the user's device, operating system, and browser.
   *
   * @return {UAParserOriginalResult} An object containing the parsed user agent details such as browser name, version, OS, and device type.
   */
  private parseUserAgent(): UAParserOriginalResult {
    const parser = new UAParser();
    return parser.getResult();
  }

  /**
   * Transforms the result from the UAParser library into a standardized format.
   *
   * @param {UAParserOriginalResult} result - The raw result from UAParser that includes detailed information about the user agent.
   * @return {UAParserResult} A transformed object containing structured details about the user agent, browser, engine, OS, device, and CPU architecture.
   */
  private transformUAParserResult(result: UAParserOriginalResult): UAParserResult {
    return {
      ua: result.ua || "",
      browser: {
        name: result.browser.name || "",
        version: result.browser.version || "",
        major: result.browser.major || "",
      },
      engine: {
        name: result.engine.name || "",
        version: result.engine.version || "",
      },
      os: {
        name: result.os.name || "",
        version: result.os.version || "",
      },
      device: {
        vendor: result.device.vendor || "",
        model: result.device.model || "",
        type: result.device.type || "",
      },
      cpu: {
        architecture: result.cpu.architecture || "",
      },
    };
  }
}

export interface UAParserResult {
  ua: string;
  browser: {
    name: string;        // Browser name, e.g., "Chrome"
    version: string;     // Browser full version, e.g., "89.0.4389.82"
    major: string;       // Browser major version, e.g., "89"
  };
  engine: {
    name: string;        // Engine name, e.g., "Blink"
    version: string;     // Engine version, e.g., "89.0.4389.82"
  };
  os: {
    name: string;        // OS name, e.g., "Windows"
    version: string;     // OS version, e.g., "10"
  };
  device: {
    vendor: string;      // Device vendor, e.g., "Apple"
    model: string;       // Device model, e.g., "iPhone"
    type: string;        // Device type, e.g., "mobile"
  };
  cpu: {
    architecture: string; // CPU architecture, e.g., "amd64"
  };
}

